<script>
import DashboardApexChart from './DashboardApexChart.vue';
import PartnerReportFilesPanel from './partner-report/PartnerReportFilesPanel.vue';
import PartnerReportGenerateBar from './partner-report/PartnerReportGenerateBar.vue';
import dashboardKpiApi from './mixins/dashboardKpiApi';
import dashboardPeriodToolbar from './mixins/dashboardPeriodToolbar';
import './partner-report/partner-report-means.css';

export default {
    name: 'PartnerReportMeansViewer',
    components: {
        DashboardApexChart,
        PartnerReportFilesPanel,
        PartnerReportGenerateBar,
    },
    mixins: [dashboardKpiApi, dashboardPeriodToolbar],
    props: {
        autoFetch: {
            type: Boolean,
            default: false,
        },
        chartable: {
            type: Boolean,
            default: true,
        },
        chartType: {
            type: String,
            default: 'line',
        },
        usePartnerDefaults: {
            type: Boolean,
            default: true,
        },
        generateUrl: {
            type: String,
            default: '',
        },
        canGenerate: {
            type: Boolean,
            default: false,
        },
        exportsIndexUrl: {
            type: String,
            default: '',
        },
        fileDownloadBaseUrl: {
            type: String,
            default: '',
        },
        exportStatusUrlTemplate: {
            type: String,
            default: '',
        },
        reportKind: {
            type: String,
            default: '',
        },
        widgetDomain: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            echoChannelName: null,
        };
    },
    computed: {
        columns() {
            return this.kpiPayload?.columns ?? [];
        },
        rows() {
            return this.kpiPayload?.rows ?? [];
        },
        kpiChart() {
            return this.kpiPayload?.chart ?? null;
        },
        isEmpty() {
            return !this.kpiLoading && this.rows.length === 0;
        },
        hasPlaceholderOnly() {
            return this.rows.length === 1
                && !this.rows[0]?.year_month
                && Number(this.rows[0]?.sample_count ?? 0) === 0;
        },
        monthsMissing() {
            return this.kpiPayload?.meta?.months_missing ?? [];
        },
        monthsPresent() {
            return this.kpiPayload?.meta?.months_present ?? [];
        },
        detailMonthsPresent() {
            return this.kpiPayload?.meta?.detail_months_present ?? [];
        },
        materializedBackfillHint() {
            return this.kpiPayload?.meta?.materialized_backfill_hint ?? '';
        },
        partnerFloor() {
            return this.kpiPayload?.meta?.partner_floor ?? null;
        },
        returnsBeforeHomogeneousWindow() {
            return Boolean(this.kpiPayload?.meta?.returns_before_homogeneous_window);
        },
        defaultGenerateMonth() {
            const candidate = this.to || this.from || '';

            if (typeof candidate !== 'string' || candidate === '') {
                return '';
            }

            if (/^\d{4}-\d{2}$/.test(candidate)) {
                return candidate;
            }

            if (/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
                return candidate.slice(0, 7);
            }

            return '';
        },
        showMissingMonthsBanner() {
            return this.monthsMissing.length > 0;
        },
        showMaterializedEmptyHint() {
            return this.hasPlaceholderOnly && this.monthsMissing.length > 0;
        },
        effectiveChartType() {
            const fromMeta = this.kpiPayload?.meta?.chart_type;
            if (fromMeta === 'scatter' || fromMeta === 'line') {
                return fromMeta;
            }

            const categories = this.kpiChart?.categories ?? [];
            if (categories.length <= 1) {
                return 'scatter';
            }

            return this.chartType;
        },
        periodTotalRow() {
            return this.rows.find((row) => row.brand === 'TOTAL' && !row.year_month) ?? null;
        },
        cardTitle() {
            if (this.reportKind === 'returns') {
                return this._t('UMG returns means card title');
            }

            return this._t('UMG shipping means card title');
        },
        detailRowCount() {
            return this.rows.filter((row) => !(row.brand === 'TOTAL' && !row.year_month)).length;
        },
    },
    mounted() {
        if (!this.usePartnerDefaults && !this.from && !this.to && this.preset !== 'all') {
            this.setPresetDates(this.preset);
        }

        this.fetchKpi(this.buildPeriodParams());
        this.subscribeUmgKpiNotifications();
    },
    beforeDestroy() {
        this.unsubscribeUmgKpiNotifications();
    },
    methods: {
        subscribeUmgKpiNotifications() {
            const userId = window.Laravel?.userId;

            if (!window.Echo || !userId) {
                return;
            }

            this.echoChannelName = `App.User.${userId}`;
            window.Echo.private(this.echoChannelName)
                .listen('.in-app-notification.created', this.onInAppNotificationCreated);
        },
        unsubscribeUmgKpiNotifications() {
            if (!window.Echo || !this.echoChannelName) {
                return;
            }

            window.Echo.private(this.echoChannelName)
                .stopListening('.in-app-notification.created', this.onInAppNotificationCreated);
        },
        onInAppNotificationCreated(payload) {
            if (!payload || payload.type !== 'umg_partner_kpi_ready') {
                return;
            }

            let meta = {};

            if (typeof payload.body === 'string' && payload.body !== '') {
                try {
                    meta = JSON.parse(payload.body);
                } catch (error) {
                    meta = {};
                }
            }

            const reportType = meta.report_type || meta.reportType;

            if (reportType && reportType !== this.reportKind) {
                return;
            }

            this.onExportFinished(payload);
        },
        onExportFinished() {
            this.fetchKpi(this.buildPeriodParams());
        },
        formatInteger(value) {
            const number = Number(value ?? 0);

            if (Number.isNaN(number)) {
                return String(value ?? '');
            }

            return new Intl.NumberFormat('it-IT').format(number);
        },
        formatMean(value) {
            const number = Number(value);

            if (Number.isNaN(number)) {
                return String(value ?? '');
            }

            return number.toLocaleString('it-IT', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
            });
        },
        isTotalRow(row) {
            return row.brand === 'TOTAL' && !row.year_month;
        },
        columnLabel(column) {
            const labels = {
                year_month: 'Year month',
                brand: 'Brand',
                sample_count: 'Samples',
                mean_days: 'Mean (days)',
            };

            return this._t(labels[column] ?? column);
        },
    },
};
</script>

<template>
    <div class="umg-means-card partner-report-means-viewer" :class="'umg-means-card--' + reportKind">
        <div class="umg-means-card__header">
            <div class="umg-means-card__heading">
                <span class="umg-means-card__accent" aria-hidden="true" />
                <div>
                    <h3 class="umg-means-card__title">{{ cardTitle }}</h3>
                    <p class="umg-means-card__subtitle">{{ _t('UMG partner subtitle') }}</p>
                </div>
            </div>
            <div class="umg-means-card__toolbar partner-report-means-viewer__toolbar">
                <label class="umg-means-card__toolbar-field">
                    <span class="umg-means-card__toolbar-label">{{ _t('Period') }}</span>
                    <select
                        v-model="preset"
                        class="form-control input-sm kpi-viewer__preset"
                        @change="onPresetChange"
                    >
                        <template v-if="usePartnerDefaults">
                            <option value="current">{{ presetLabels.lastMonth }}</option>
                            <option value="3">{{ presetLabels.last3Months }}</option>
                            <option value="6">{{ presetLabels.last6Months }}</option>
                            <option value="custom">{{ presetLabels.custom }}</option>
                        </template>
                        <template v-else>
                            <option value="7">{{ presetLabels.last7 }}</option>
                            <option value="30">{{ presetLabels.last30 }}</option>
                            <option value="90">{{ presetLabels.last90 }}</option>
                            <option value="year">{{ presetLabels.year }}</option>
                            <option value="all">{{ presetLabels.all }}</option>
                        </template>
                    </select>
                </label>
                <template v-if="preset === 'custom'">
                    <label class="umg-means-card__toolbar-field">
                        <span class="umg-means-card__toolbar-label">{{ _t('From') }}</span>
                        <input
                            v-model="from"
                            :type="usePartnerDefaults ? 'month' : 'date'"
                            class="form-control input-sm"
                        />
                    </label>
                    <label class="umg-means-card__toolbar-field">
                        <span class="umg-means-card__toolbar-label">{{ _t('To') }}</span>
                        <input
                            v-model="to"
                            :type="usePartnerDefaults ? 'month' : 'date'"
                            class="form-control input-sm"
                        />
                    </label>
                    <button
                        type="button"
                        class="btn btn-default btn-sm"
                        :disabled="kpiLoading"
                        @click="fetchKpi(buildPeriodParams())"
                    >
                        {{ presetLabels.apply }}
                    </button>
                </template>
                <button
                    type="button"
                    class="btn btn-default btn-sm kpi-viewer__refresh"
                    :disabled="kpiLoading"
                    :title="_t('Refresh')"
                    @click="refreshPeriod"
                >
                    <i class="fa" :class="kpiLoading ? 'fa-spinner fa-spin' : 'fa-refresh'" />
                </button>
            </div>
        </div>

        <div class="kpi-stage" :aria-busy="kpiLoading ? 'true' : 'false'">
            <div class="umg-means-card__kpis">
            <div class="umg-means-card__kpi">
                <p class="umg-means-card__kpi-label">{{ _t('Period mean (days)') }}</p>
                <div class="umg-means-card__kpi-value umg-means-card__kpi-value--with-unit">
                    <span>{{ periodTotalRow ? formatMean(periodTotalRow.mean_days) : '\u00a0' }}</span>
                    <span v-if="periodTotalRow" class="umg-means-card__kpi-unit">{{ _t('days_abbr') }}</span>
                </div>
            </div>
            <div class="umg-means-card__kpi">
                <p class="umg-means-card__kpi-label">{{ _t('Samples') }}</p>
                <p class="umg-means-card__kpi-value">{{ periodTotalRow ? formatInteger(periodTotalRow.sample_count) : '\u00a0' }}</p>
            </div>
            <div class="umg-means-card__kpi">
                <p class="umg-means-card__kpi-label">{{ _t('Months present') }}</p>
                <p class="umg-means-card__kpi-value">{{ periodTotalRow ? monthsPresent.length : '\u00a0' }}</p>
            </div>
            </div>

            <div v-if="kpiError" class="umg-means-card__section text-danger">{{ kpiError }}</div>
            <div v-if="chartable" class="umg-means-card__section">
            <div class="umg-means-card__section-head">
                <h4 class="umg-means-card__section-title">{{ _t('Trend') }}</h4>
            </div>
            <div class="box box-primary dashboard-chart-panel umg-means-card__chart-panel">
                <div class="box-body">
                    <dashboard-apex-chart
                        v-if="kpiChart"
                        :chart="kpiChart"
                        :chart-type="effectiveChartType"
                    />
                    <div v-else class="dashboard-apex-chart" aria-hidden="true" />
                </div>
            </div>
            </div>

            <div class="umg-means-card__section">
            <div class="umg-means-card__section-head">
                <h4 class="umg-means-card__section-title">{{ _t('Brand detail table') }}</h4>
                <span v-if="kpiPayload && !isEmpty && !hasPlaceholderOnly" class="umg-means-card__section-meta">
                    {{ detailRowCount }} {{ _t('rows') }}
                </span>
            </div>

            <div
                class="umg-means-card__table-scroll"
                role="region"
                :aria-label="_t('Means detail table')"
                tabindex="0"
            >
                <div v-if="showMissingMonthsBanner" class="umg-means-card__banner" role="status">
                    <i class="fa fa-exclamation-triangle umg-means-card__banner-icon" aria-hidden="true" />
                    <div>
                        <div class="umg-means-card__banner-title">
                            {{ _t('Missing materialized months') }}: {{ monthsMissing.join(', ') }}
                        </div>
                        <p v-if="materializedBackfillHint" class="umg-means-card__banner-text">
                            {{ materializedBackfillHint }}
                        </p>
                    </div>
                </div>
                <p v-if="isEmpty || hasPlaceholderOnly" class="umg-means-card__empty text-muted">
                    <template v-if="!kpiPayload">&nbsp;</template>
                    <template v-else-if="showMaterializedEmptyHint">
                        {{ _t('No materialized CSV data for the selected months.') }}
                        <span v-if="materializedBackfillHint">{{ materializedBackfillHint }}</span>
                    </template>
                    <template v-else-if="returnsBeforeHomogeneousWindow && partnerFloor">
                        {{ _t('No samples in the selected period.') }}
                        {{ _t('Homogeneous returns KPI from') }} {{ partnerFloor }}.
                    </template>
                    <template v-else>
                        {{ _t('No samples in the selected period.') }}
                    </template>
                </p>
                <table v-else class="table umg-means-card__table">
                    <thead>
                        <tr>
                            <th v-for="column in columns" :key="column">{{ columnLabel(column) }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(row, index) in rows"
                            :key="index"
                            :class="{ 'umg-means-card__row-total': isTotalRow(row) }"
                        >
                            <td v-for="column in columns" :key="column">{{ row[column] }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            </div>

            <partner-report-files-panel
                :months-present="monthsPresent"
                :months-missing="monthsMissing"
                :detail-months-present="detailMonthsPresent"
                :file-download-base-url="fileDownloadBaseUrl"
            />

            <div v-if="kpiLoading" class="kpi-stage__overlay">
                <div class="kpi-stage__overlay-inner">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true" />
                    <span class="kpi-stage__overlay-label">{{ _t('Loading...') }}</span>
                </div>
            </div>
        </div>

        <div class="umg-means-card__generate">
            <partner-report-generate-bar
                :can-generate="canGenerate"
                :generate-url="generateUrl"
                :default-generate-month="defaultGenerateMonth"
                :exports-index-url="exportsIndexUrl"
                :export-status-url-template="exportStatusUrlTemplate"
                :kpi-loading="kpiLoading"
            />
        </div>
    </div>
</template>
